import { useState } from "react";
import { NavLink, Navigate, Route, Routes, Link } from "react-router-dom";
import { toast } from "sonner";
import { LayoutDashboard, ShoppingCart, Package, Boxes, Truck, Users, Megaphone, MessageSquare, BarChart3, Settings, LogOut, Repeat } from "lucide-react";
import { useAuth, can } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { Logo } from "@/components/store/Bits";
import Dashboard from "./Dashboard";
import Orders from "./Orders";
import Products from "./Products";
import Stock, { Suppliers } from "./Stock";
import Customers, { Subscriptions } from "./Customers";
import Marketing from "./Marketing";
import Care from "./Care";
import Reports from "./Reports";
import SettingsPage from "./Settings";

const MENU = [
  ["", "Painel", LayoutDashboard, "dashboard"], ["pedidos", "Pedidos", ShoppingCart, "orders"], ["produtos", "Produtos", Package, "products"],
  ["estoque", "Estoque e lotes", Boxes, "stock"], ["fornecedores", "Fornecedores", Truck, "suppliers"], ["clientes", "Clientes", Users, "customers"],
  ["assinaturas", "Assinaturas", Repeat, "subscriptions"], ["marketing", "Marketing e conteúdo", Megaphone, "coupons"],
  ["atendimento", "Avaliações e trocas", MessageSquare, "returns"], ["relatorios", "Relatórios", BarChart3, "reports"], ["configuracoes", "Configurações", Settings, "settings"],
];
const visible = (user, perm) => can(user, perm) || (perm === "coupons" && (can(user, "content") || can(user, "blog"))) || (perm === "returns" && can(user, "reviews"));

function Login() {
  const { login } = useAuth();
  const [f, setF] = useState({ email: "", senha: "" });
  const go = async (e) => { e.preventDefault(); try { await login(f.email.trim(), f.senha); } catch (er) { toast.error(errMsg(er)); } };
  return (
    <div className="flex min-h-screen items-center justify-center bg-na-forest p-4">
      <form onSubmit={go} className="w-full max-w-sm rounded-[28px] bg-white p-8" data-testid="admin-login-form">
        <Logo /><h1 className="mt-6 text-3xl">Admin</h1>
        <label htmlFor="a-email" className="label-na mt-6">E-mail</label><input id="a-email" type="email" className="input-na" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="admin-login-email" />
        <label htmlFor="a-pw" className="label-na mt-4">Senha</label><input id="a-pw" type="password" className="input-na" value={f.senha} onChange={(e) => setF({ ...f, senha: e.target.value })} data-testid="admin-login-password" />
        <button className="btn-na mt-6 w-full" data-testid="admin-login-submit">Entrar</button>
        <Link to="/redefinir-senha" className="mt-4 block text-center text-sm font-semibold text-na-forest underline" data-testid="admin-forgot-password-link">Esqueci minha senha</Link>
      </form>
    </div>
  );
}

function ChangePassword() {
  const { setUser } = useAuth();
  const [f, setF] = useState({ senha_atual: "", nova_senha: "" });
  const go = async (e) => { e.preventDefault(); try { const r = await api.post("/auth/change-password", f); setUser(r.data); toast.success("Senha alterada"); } catch (er) { toast.error(errMsg(er)); } };
  return (
    <div className="flex min-h-screen items-center justify-center bg-na-bg p-4">
      <form onSubmit={go} className="card-na w-full max-w-sm p-8" data-testid="change-password-form">
        <h1 className="text-3xl">Troque sua senha</h1><p className="mt-2 text-sm text-na-ink2">Por segurança, defina uma nova senha no primeiro acesso (10+ caracteres).</p>
        <label htmlFor="cp-a" className="label-na mt-6">Senha atual</label><input id="cp-a" type="password" className="input-na" value={f.senha_atual} onChange={(e) => setF({ ...f, senha_atual: e.target.value })} data-testid="current-password" />
        <label htmlFor="cp-n" className="label-na mt-4">Nova senha</label><input id="cp-n" type="password" className="input-na" value={f.nova_senha} onChange={(e) => setF({ ...f, nova_senha: e.target.value })} data-testid="new-password" />
        <button className="btn-na mt-6 w-full" data-testid="change-password-submit">Salvar nova senha</button>
      </form>
    </div>
  );
}

export default function AdminApp() {
  const { user, logout } = useAuth();
  if (user === null) return <div className="p-10">Carregando...</div>;
  if (!user) return <Login />;
  if (user.papel === "cliente") return <Navigate to="/conta" replace />;
  if (user.trocar_senha) return <ChangePassword />;
  const items = MENU.filter((m) => visible(user, m[3]));
  return (
    <div className="min-h-screen bg-na-bg lg:flex">
      <aside className="sticky top-0 z-30 bg-na-forest text-white lg:h-screen lg:w-64 lg:shrink-0 lg:overflow-y-auto" data-testid="admin-sidebar">
        <div className="hidden p-6 lg:block"><Logo light /><p className="mt-2 text-xs text-white/60">{user.email} · {user.papel}</p></div>
        <nav className="no-scrollbar flex gap-1 overflow-x-auto px-3 py-2 lg:flex-col lg:py-0">
          {items.map(([to, label, I]) => (
            <NavLink key={to} to={`/admin/${to}`} end={to === ""} data-testid={`admin-nav-${to || "painel"}`}
              className={({ isActive }) => `flex shrink-0 items-center gap-3 whitespace-nowrap rounded-full px-4 py-2.5 text-sm transition-colors ${isActive ? "bg-white text-na-forest" : "text-white/80 hover:bg-white/10"}`}>
              <I className="h-4 w-4" />{label}
            </NavLink>
          ))}
          <Link to="/" className="flex shrink-0 items-center gap-3 rounded-full px-4 py-2.5 text-sm text-white/60 hover:bg-white/10">Ver loja</Link>
          <button onClick={logout} className="flex shrink-0 items-center gap-3 rounded-full px-4 py-2.5 text-sm text-white/60 hover:bg-white/10" data-testid="admin-logout"><LogOut className="h-4 w-4" />Sair</button>
        </nav>
      </aside>
      <main className="min-w-0 flex-1 p-4 sm:p-8">
        <Routes>
          <Route index element={<Dashboard />} />
          <Route path="pedidos" element={<Orders />} />
          <Route path="produtos" element={<Products />} />
          <Route path="estoque" element={<Stock />} />
          <Route path="fornecedores" element={<Suppliers />} />
          <Route path="clientes" element={<Customers />} />
          <Route path="assinaturas" element={<Subscriptions />} />
          <Route path="marketing" element={<Marketing />} />
          <Route path="atendimento" element={<Care />} />
          <Route path="relatorios" element={<Reports />} />
          <Route path="configuracoes" element={<SettingsPage />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </main>
    </div>
  );
}
